import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2 } from "lucide-react";

const ease = [0.76, 0, 0.24, 1];

/**
 * Themed gallery of program deliverable examples with lightbox.
 * Props:
 *  - title, subtitle
 *  - images: [{ src, caption }]
 *  - theme: { accent, text, textMuted, border, borderStrong, pill, cardBg, bg }
 *  - testid: base data-testid, cards get `${testid}-{i}`, lightbox = `${testid}-lightbox`
 */
export default function ExampleGallery({
  title,
  subtitle,
  images,
  theme,
  testid = "gallery",
}) {
  const [active, setActive] = useState(null);

  return (
    <div className="mt-14 md:mt-16">
      <div
        className="pt-10 md:pt-12 border-t mb-8"
        style={{ borderColor: theme.border }}
      >
        <div
          className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-3"
          style={{ color: theme.accent }}
        >
          Real Examples
        </div>
        <h4
          className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em]"
          style={{ fontVariationSettings: '"SOFT" 60', color: theme.text }}
        >
          {title}
        </h4>
        {subtitle && (
          <p
            className="mt-4 text-base md:text-lg max-w-2xl leading-relaxed font-light"
            style={{ color: theme.textMuted }}
          >
            {subtitle}
          </p>
        )}
      </div>

      <div
        className={`grid gap-4 md:gap-6 ${
          images.length === 1
            ? "grid-cols-1 max-w-5xl mx-auto"
            : images.length === 2
              ? "grid-cols-1 md:grid-cols-2"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        {images.map((img, i) => (
          <motion.button
            key={img.src}
            onClick={() => setActive(img)}
            data-testid={`${testid}-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease }}
            className="group text-left block w-full focus:outline-none rounded-2xl overflow-hidden"
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
            }}
            aria-label={`View ${img.caption}`}
          >
            <div
              className="relative overflow-hidden p-4 md:p-6 flex items-center justify-center"
              style={{ background: theme.bgSoft || theme.bg }}
            >
              <img
                src={img.src}
                alt={img.caption}
                loading="lazy"
                className="max-w-full h-auto object-contain transition-transform duration-[900ms] ease-out group-hover:scale-[1.02] rounded-lg"
                style={{ maxHeight: images.length === 1 ? "640px" : "460px" }}
              />
              <div
                className="absolute top-3 left-3 text-[10px] tracking-[0.28em] uppercase font-semibold px-2.5 py-1 rounded-full pointer-events-none"
                style={{
                  background: theme.accent,
                  color: theme.bg,
                }}
              >
                Sample · {String(i + 1).padStart(2, "0")}
              </div>
              <div
                className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-semibold pointer-events-none"
                style={{
                  background: theme.accent,
                  color: theme.bg,
                }}
              >
                <Maximize2 size={12} strokeWidth={2} /> Zoom
              </div>
            </div>
            <div className="p-4 md:p-5">
              <p
                className="text-sm md:text-[15px] font-light leading-relaxed"
                style={{ color: theme.textMuted }}
              >
                {img.caption}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setActive(null)}
            data-testid={`${testid}-lightbox`}
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 md:top-8 md:right-8 w-12 h-12 border border-white/40 flex items-center justify-center text-white hover:text-saffron hover:border-saffron transition-colors rounded-full"
              aria-label="Close"
              data-testid={`${testid}-lightbox-close`}
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.4, ease }}
              className="max-w-full max-h-full flex flex-col items-center gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.src}
                alt={active.caption}
                className="max-w-full max-h-[80vh] object-contain shadow-2xl rounded-xl"
                style={{ border: `1px solid ${theme.borderStrong}` }}
              />
              <p className="text-white/80 text-sm md:text-base max-w-2xl text-center font-light">
                {active.caption}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
